import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { Button, Input, Card, Typography, Row, Col, Form, Empty, Tooltip, Segmented, Table, message, Avatar, Badge } from 'antd';
import { PlusOutlined, SearchOutlined, DeleteOutlined, TeamOutlined, CarOutlined, CalendarOutlined, SolutionOutlined, HeartOutlined, HeartFilled, ThunderboltOutlined, BookOutlined, AppstoreOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import BatchFormModal from '../../components/modals/batchFormModal';
import FilterPopover from '../../components/filter';
import CalendarBatchView from './calendarBatchView';
import { batchActions } from '../../actions/batchActions';
import { useDispatch, useSelector } from 'react-redux';
import Paginations from '../../components/pagination';
import ConfirmModal from '../../components/confirmModal';
import { userActions } from '../../actions/userActions';
import { batchService } from '../../services/batchService';
import { getblobLink, useCurrentPageAccess } from '../../services/helper';
import GuardedButton, { useSubscriptionGuard } from '../../components/guardedButton';
import SpinnerLoader from '../../components/spinnerLoader';

const { Text } = Typography;

interface Batch {
  _id: string;
  key: string;
  batchName: string;
  instructorId?: {
    _id: string;
    firstName: string;
    lastName: string;
  };
  staffId?: {
    _id: string;
    firstName: string;
    lastName: string;
  };
  trainingType?: 'SIMULATION' | 'ON_ROAD' | null;
  studentIds?: Array<{
    _id: string;
    firstName: string;
    lastName: string;
  }>;
  startTime: string;
  endTime: string;
  status: 'ACTIVE' | 'INACTIVE' | 'COMPLETED';
  isActive?: boolean;
}

interface batchProps {
  role?: string;
}

interface ApiBatch {
  _id: string;
  batchName: string;
  instructorId?: any;
  studentIds?: any[];
  startTime: string;
  endTime: string;
  status: string;
}

const Batches: React.FC<batchProps> = ({ role }) => {

  const mountCount = useRef(0);
  const navigate = useNavigate();
  const pageAccess = useCurrentPageAccess();
  const { guard } = useSubscriptionGuard();
  const [batches, setBatches] = useState<Batch[]>([]);
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState<'ALL' | 'ACTIVE' | 'INACTIVE' | 'COMPLETED'>('ALL');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editing, setEditing] = useState<any | null>(null);
  const [form] = Form.useForm();
  const [filterData, setIsFilterData] = useState({ status: '', instructorId: '' });
  const [recordToDelete, setRecordToDelete] = useState<string>('');
  const [appliedFilters, setAppliedFilters] = useState<Record<string, any>>({});
  const [mutableBatch, setMutableBatch] = useState<any[]>([]);
  const [debouncedSearchText, setDebouncedSearchText] = useState('');
  const [visible, setVisible] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState<any | null>(null);
  const [pendingUserFetch, setPendingUserFetch] = useState<'INSTRUCTOR' | 'STUDENT' | null>(null);
  const [viewLoading, setViewLoading] = useState(false);
  const [availableInstructors, setAvailableInstructors] = useState<any[]>([]);
  const [availableStudents, setAvailableStudents] = useState<any[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [isFilter, setIsFilter] = useState(false);
  const [batchesRowWidth, setBatchesRowWidth] = useState(0);
  const [attendanceData, setAttendanceData] = useState<Record<string, 'PRESENT' | 'ABSENT' | null>>({});
  const [previousAttendanceData, setPreviousAttendanceData] = useState<Record<string, 'PRESENT' | 'ABSENT' | null>>({});
  const [pendingAttendanceUpdate, setPendingAttendanceUpdate] = useState<string | null>(null);
  const [statusCountsData, setStatusCountsData] = useState<Record<string, number>>({ ALL: 0, ACTIVE: 0, INACTIVE: 0, COMPLETED: 0 });
  const [viewportWidth, setViewportWidth] = useState<number>(() => window.innerWidth);
  const [viewMode, setViewMode] = useState<'grid' | 'calendar'>(() => {
    const saved = sessionStorage.getItem('batches_viewMode');
    if (saved === 'grid' || saved === 'calendar') return saved;
    return pageAccess.isCalendarView ? 'calendar' : 'grid';
  });
  const prevViewMode = useRef(viewMode);
  const defaultPayLoad = { page: 1, limit: 10 };
  const [pageSize, setPageSize] = useState<number>(Number(defaultPayLoad.limit));
  const [currentPage, setCurrentPage] = useState<number>(1);
  const dispatch = useDispatch();
  const roleData = useSelector((state: any) => state.roleDataReducer.roleData);
  const getBatch = useSelector((state: any) => state.getBatchParamsReducer);
  const batchData = useSelector((state: any) => state.createBatchReducer);
  const deleteBatchData = useSelector((state: any) => state.deleteBatchReducer);
  const availableUsersState = useSelector((state: any) => state.getAvailableUsersReducer);
  const availableStudentsState = useSelector((state: any) => state.getAvailableStudentsReducer);
  const toggleBatchState = useSelector((state: any) => state.toggleBatchReducer);
  const markAttendanceState = useSelector((state: any) => state.markAttendanceReducer);
  const instructors = useSelector((state: any) => state.userListReducer.userList);
  const [initialLoaded, setInitialLoaded] = useState(false);
  const [isTabSwitching, setIsTabSwitching] = useState(false);

  useEffect(() => {

    const getLimitForWidth = (width: number) => {
      if (width < 768) return 6;
      if (width > 992 && width <= 1280) return 6;
      return 10;
    };

    const handleResize = () => {
      const newLimit = getLimitForWidth(window.innerWidth);
      setPageSize(prev => {
        if (prev !== newLimit) {
          setCurrentPage(1);
          return newLimit;
        }
        return prev;
      });
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);


  useEffect(() => {
    dispatch(userActions.userRoleRequest());
  }, []);

  useEffect(() => {
    if (roleData && roleData.length > 0) {
      const instructorRole = roleData.find((r: any) => r.role === 'INSTRUCTOR');
      if (instructorRole?._id) {
        dispatch(userActions.userListRequest({ role: instructorRole._id }));
      }
    }
  }, [roleData]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearchText(searchText.trim());
    }, 1000);
    return () => window.clearTimeout(timer);
  }, [searchText]);


  useEffect(() => {
    mountCount.current += 1;
    const isViewModeSwitch = prevViewMode.current !== viewMode;
    prevViewMode.current = viewMode;
    if (mountCount.current === 2 && currentPage === 1 && !debouncedSearchText?.trim() && !isViewModeSwitch) {
      return;
    }

    if (isViewModeSwitch && viewMode === 'grid') {
      setCurrentPage(1);
      setMutableBatch([]);
    }

    const payload: any = {
      pageNumber: isViewModeSwitch && viewMode === 'grid' ? 1 : currentPage,
      pageSize: viewMode === 'calendar' ? 100 : pageSize
    };

    if (debouncedSearchText?.trim()) {
      payload.searchText = debouncedSearchText.trim();
    }
    Object.entries(filterData).forEach(([k, v]) => {
      if (v !== undefined && v !== null && String(v).trim() !== '') {
        payload[k] = v;
      }
    });
    dispatch(batchActions.getBatchParams(payload));
  }, [currentPage, pageSize, debouncedSearchText, filterData, roleData, viewMode]);

  useEffect(() => {
    if (batchData && batchData?.isSuccess) {
      setIsModalOpen(false);
      setEditing(null);
      dispatch(batchActions.getBatchParams({ pageNumber: currentPage, pageSize }));
      batchData.isSuccess = false;
    }
    if (deleteBatchData && deleteBatchData?.isSuccess) {
      dispatch(batchActions.getBatchParams({ pageNumber: currentPage, pageSize }));
      deleteBatchData.isSuccess = false;
      setVisible(false);
    }
    if (getBatch && getBatch.isSuccess) {
      const batch = getBatch?.batchdata?.batches ?? null;
      setMutableBatch(batch);
      setIsTabSwitching(false);
    }
    if (getBatch && getBatch.isError) {
      setIsTabSwitching(false);
    }
    if (instructors) {
      setAvailableInstructors(instructors?.users);
    }
  }, [batchData, deleteBatchData, getBatch, instructors, dispatch, currentPage, pageSize]);


  const fetchStatusCounts = useCallback(async (instructorIdVal?: string, searchVal?: string) => {
    const statusesToFetch = ['ALL', 'ACTIVE', 'INACTIVE', 'COMPLETED'];
    const counts: Record<string, number> = { ALL: 0, ACTIVE: 0, INACTIVE: 0, COMPLETED: 0 };

    const baseParams: any = { pageNumber: 1, pageSize: 1 };
    const search = searchVal !== undefined ? searchVal : debouncedSearchText;
    const instructor = instructorIdVal !== undefined ? instructorIdVal : filterData.instructorId;

    if (search?.trim()) {
      baseParams.searchText = search.trim();
    }
    if (instructor) {
      baseParams.instructorId = instructor;
    }

    await Promise.all(statusesToFetch.map(async (status: string) => {
      try {
        const params: any = { ...baseParams };
        if (status !== 'ALL') {
          params.status = status;
        }
        const resp: any = await batchService.getBatchParams(params);
        const resultObj = resp?.data?.batchdata ?? resp?.batchdata ?? resp?.data?.resultObject ?? resp?.resultObject ?? resp?.data ?? resp;
        counts[status] = resultObj?.pagination?.totalRecords ?? resultObj?.totalRecords ?? resultObj?.totalCount ?? (Array.isArray(resultObj?.batches) ? resultObj.batches.length : 0);
      } catch (e) {
        counts[status] = 0;
      }
    }));

    setStatusCountsData(counts);
  }, [debouncedSearchText, filterData.instructorId]);

  // Re-fetch tab counts when filters, search, create, or delete occur
  useEffect(() => {
    fetchStatusCounts(filterData.instructorId, debouncedSearchText);
  }, [filterData.instructorId, debouncedSearchText, batchData?.isSuccess, deleteBatchData?.isSuccess, fetchStatusCounts]);


  const apiBatches: ApiBatch[] = useMemo(() => {
    const data = getBatch?.batchdata;
    if (!data) return [];
    return Array.isArray(data?.batches) ? data.batches : [];
  }, [getBatch?.batchdata]);

  useEffect(() => {
    const mapped: Batch[] = apiBatches.map((b: ApiBatch) => ({
      key: b._id,
      _id: b._id,
      batchName: b.batchName,
      instructorId: b.instructorId,
      staffId: (b as any).staffId,
      trainingType: (b as any).trainingType,
      studentIds: b.studentIds,
      startTime: b.startTime,
      endTime: b.endTime,
      status: b.status as any,
      isActive: b.status === 'ACTIVE',
    }));
    setBatches(mapped);
  }, [apiBatches]);

  const handleSetViewMode = (mode: 'grid' | 'calendar') => {
    sessionStorage.setItem('batches_viewMode', mode);
    setViewMode(mode);
  };

  useEffect(() => {
    if (!pageAccess.isCalendarView) {
      sessionStorage.setItem('batches_viewMode', 'grid');
      setViewMode('grid');
    }
  }, [pageAccess]);


  const filtered = useMemo(() => {
    const txt = searchText.toLowerCase();
    return mutableBatch.filter((b: any) => {
      const statusMatch = statusFilter === 'ALL' || b.status === statusFilter;
      const name = String(b.batchName ?? '').toLowerCase();
      let instructorName = '';
      if (b.instructorName) {
        instructorName = String(b.instructorName).toLowerCase();
      } else if (b.instructorId && typeof b.instructorId === 'object') {
        instructorName = `${b.instructorId.firstName ?? ''} ${b.instructorId.lastName ?? ''}`.toLowerCase();
      }
      const searchMatch = !txt || name.includes(txt) || instructorName.includes(txt);

      return statusMatch && searchMatch;
    });
  }, [mutableBatch, searchText, statusFilter]);

  const statusCounts = useMemo(() => {
    return {
      ALL: statusCountsData.ALL,
      ACTIVE: statusCountsData.ACTIVE,
      INACTIVE: statusCountsData.INACTIVE,
      COMPLETED: statusCountsData.COMPLETED,
    };
  }, [statusCountsData]);



  useEffect(() => {
    if (!availableUsersState?.isSuccess) return;

    const instructors = availableUsersState?.availableUsers?.users ?? [];

    if (pendingUserFetch === "INSTRUCTOR") {
      setAvailableInstructors(instructors);
      setPendingUserFetch("STUDENT");
      return;
    }
  }, [availableUsersState, pendingUserFetch]);



  useEffect(() => {
    if (!availableStudentsState?.isSuccess) return;

    const students =
      availableStudentsState?.availableStudents?.users ?? [];

    if (pendingUserFetch === "STUDENT") {
      setAvailableStudents(students);
      setPendingUserFetch(null);
      setLoadingUsers(false);
    }

  }, [availableStudentsState, pendingUserFetch]);


  useEffect(() => {
    if (toggleBatchState?.isSuccess) {
      dispatch(batchActions.getBatchParams({ pageNumber: currentPage, pageSize }));
    }
  }, [toggleBatchState, dispatch, currentPage, pageSize]);

  const handleView = (batch: any) => {
    setViewLoading(true);
    setSelectedBatch(null);
    navigate('/batches/view', { state: { batchId: batch._id } });
  };


  const showModal = (batch?: any) => {
    if (batch) {
      setEditing(batch);
      const formValues = {
        batchName: batch.batchName,
        instructorId: batch.instructorId?._id || batch.instructorId,
        staffId: batch.staffId?._id || batch.staffId,
        trainingType: batch.trainingType || undefined,
        studentIds: batch.studentIds?.map((s: any) => s._id || s) || [],
        status: batch.status,
        startTime: batch.startTime,
        endTime: batch.endTime,
        timeRange: batch.startTime && batch.endTime
          ? [dayjs(batch.startTime, 'HH:mm'), dayjs(batch.endTime, 'HH:mm')]
          : undefined,
      };
      form.setFieldsValue(formValues);
    } else {
      setEditing(null);
      form.resetFields();
    }
    setIsModalOpen(true);
  };

  const handleOnPageChange = (page: number, newPageSize?: number) => {
    setCurrentPage(page);
    if (newPageSize) setPageSize(newPageSize);
  };


  useEffect(() => {
    if (markAttendanceState?.isSuccess && markAttendanceState?.attendance) {
      const results = markAttendanceState.attendance.results || [];
      const summary = markAttendanceState.attendance.summary || { successful: 0, failed: 0, total: 0 };

      let hasFailures = false;
      results.forEach((result: any) => {
        if (!result.success) {
          hasFailures = true;
          if (pendingAttendanceUpdate === result.studentId) {
            setAttendanceData(prev => ({
              ...prev,
              [result.studentId]: previousAttendanceData[result.studentId] || null
            }));
          }

        }
      });

      if (summary.successful > 0) {
        dispatch(batchActions.viewBatch(selectedBatch?._id));
      }
      setPendingAttendanceUpdate(null);
      markAttendanceState.isSuccess = false;
    }
    if (markAttendanceState?.isError) {
      if (pendingAttendanceUpdate && previousAttendanceData[pendingAttendanceUpdate] !== undefined) {
        setAttendanceData(prev => ({
          ...prev,
          [pendingAttendanceUpdate]: previousAttendanceData[pendingAttendanceUpdate]
        }));
      }
      setPendingAttendanceUpdate(null);
      markAttendanceState.isError = false;
    }
  }, [markAttendanceState, selectedBatch, dispatch, pendingAttendanceUpdate, previousAttendanceData]);



  const handleApplyFilter = (values: any) => {
    setIsFilter(true);
    const nextPage = 1;
    setCurrentPage(nextPage);
    const newFilterData: any = { status: '', instructorId: '' };
    const appliedFiltersData: any = {};
    if (values?.status) {
      newFilterData.status = values.status;
      appliedFiltersData.status = values.status;
      setStatusFilter(values.status);
    }
    if (values?.instructorId) {
      newFilterData.instructorId = values.instructorId;
      appliedFiltersData.instructorId = values.instructorId;
    }
    setIsFilterData(newFilterData);
    setAppliedFilters(appliedFiltersData);
    fetchStatusCounts(newFilterData.instructorId, debouncedSearchText);
  };

  useEffect(() => {
    if (getBatch.isSuccess || getBatch.isError) {
      setInitialLoaded(true);
    }
  }, [getBatch.isSuccess, getBatch.isError]);

  const onFinish = (values: any) => {
    dispatch(batchActions.createBatch({
      _id: editing?._id,
      batchName: values.batchName,
      startTime: values.startTime,
      endTime: values.endTime,
      maxStudents: Number(values.maxStudents ?? 3),
      instructorId: values.instructorId,
      staffId: values.staffId,
      trainingType: values.trainingType,
      studentIds: values.studentIds,
      status: values.status,
    }));
  };

  const handleClearFilter = () => {
    setCurrentPage(1);
    setIsFilter(false);
    setIsFilterData({ status: '', instructorId: '' });
    setAppliedFilters({});
    setStatusFilter('ALL');
    fetchStatusCounts('', debouncedSearchText);
  };

  const handleStatusChange = (val: any) => {
    const value = String(val) as 'ALL' | 'ACTIVE' | 'INACTIVE' | 'COMPLETED';
    if (value === statusFilter) return;
    setIsTabSwitching(true);
    setStatusFilter(value);
    setCurrentPage(1);
    const newFilterData = { ...filterData, status: value === 'ALL' ? '' : value };
    setIsFilterData(newFilterData);
  };

  const handleDelete = () => {
    dispatch(batchActions.deleteBatch(recordToDelete));
  };

  const renderBatchCard = (batch: any) => {
    const instructorName = batch.instructorName ||
      (batch.instructorId && typeof batch.instructorId === 'object'
        ? `${batch.instructorId.firstName} ${batch.instructorId.lastName}`
        : '—');

    const studentsCount = batch.studentsCount !== undefined
      ? batch.studentsCount
      : (batch.students?.length || batch.studentIds?.length || 0);

    const studentList: any[] = batch.studentIds || batch.students || [];

    const STATUS_CONFIG: Record<string, { bg: string; color: string; dot: string }> = {
      ACTIVE: { bg: '#f0fdf4', color: '#15803d', dot: '#22c55e' },
      INACTIVE: { bg: '#f3f4f6', color: '#6b7280', dot: '#9ca3af' },
      COMPLETED: { bg: '#eff6ff', color: '#1d4ed8', dot: 'var(--primary-color)' },
    };
    const statusCfg = STATUS_CONFIG[batch.status] || STATUS_CONFIG['INACTIVE'];

    const COVER_COLORS: Record<string, string> = {
      default: '#1e293b',
    };
    const isOnRoad = batch.trainingType === 'ON_ROAD';
    const isSimulation = batch.trainingType === 'SIMULATION';
    const hasImage = isOnRoad || isSimulation;
    const coverImg = isOnRoad ? `${getblobLink()}/public/asset/onRoad.webp` : isSimulation ? `${getblobLink()}/public/asset/simulation.png` : undefined;
    const coverBg = !hasImage ? (COVER_COLORS[batch.trainingType || 'default'] || COVER_COLORS['default']) : undefined;

    const typeLabel = batch.trainingType === 'SIMULATION' ? 'Simulation' : batch.trainingType === 'ON_ROAD' ? 'On Road' : 'General';

    // Format start time for the date badge
    const timeStr = batch.startTime || '';
    const [timePart, meridiem] = timeStr.split(' ');

    return (
      <Col className="batch-card-col" key={batch._id || batch.key}>
        <Card className="batch-card" hoverable bordered={false} onClick={() => { if (!pageAccess.isView) { return; } handleView(batch) }}>

          {/* ── Cover / Hero ── */}
          <div
            className={`batch-cover${hasImage ? ' batch-cover--image' : ''}`}
            style={hasImage
              ? { backgroundImage: `url(${coverImg})` }
              : { background: coverBg }
            }
          >

            {/* Date badge top-left */}
            <div className="batch-cover-date">
              <span className="batch-cover-date__time">{timePart || '--'}</span>
              <span className="batch-cover-date__meridiem">{meridiem || ''}</span>
            </div>

            {/* Status top-right */}
            <div className="batch-cover-status" style={{ background: statusCfg.bg, color: statusCfg.color }}>
              <span className="batch-cover-status__dot" style={{ background: statusCfg.dot }} />
              {batch.status || 'ACTIVE'}
            </div>

            {/* Big center icon */}
            <div className="batch-cover-icon">
              {batch.trainingType === 'SIMULATION'
                ? <ThunderboltOutlined />
                : batch.trainingType === 'ON_ROAD'
                  ? <CarOutlined />
                  : <BookOutlined />}
            </div>

            {/* Attendees row bottom-left */}
            <div className="batch-cover-footer">
              <div className="batch-attendees">
                <Avatar.Group max={{ count: 3 }} size={26}>
                  {studentList.slice(0, 4).map((s: any, i: number) => (
                    <Avatar key={i} size={26} className="batch-attendee-avatar">
                      {(s.firstName?.[0] || s.name?.[0] || 'S').toUpperCase()}
                    </Avatar>
                  ))}
                </Avatar.Group>
                <span className="batch-attendees__label">
                  {studentsCount > 0 ? `+${studentsCount} Enrolled` : 'No students yet'}
                </span>
              </div>
            </div>
          </div>

          {/* ── Card Body ── */}
          <div className="batch-card-content">

            {/* Type pill + delete icon */}
            <div className="batch-type-pill-wrap">
              <span className={`batch-type-pill batch-type-pill--${(batch.trainingType || 'general').toLowerCase()}`}>
                {typeLabel}
              </span>
              <Tooltip title="Delete batch">
                <button
                  className="batch-delete-btn"
                  onClick={guard((e) => {
                    e.stopPropagation();
                    setRecordToDelete(batch._id || batch.key);
                    setVisible(true);
                  })}>
                  <DeleteOutlined />
                </button>
              </Tooltip>
            </div>
            <div className="batch-card-title">{batch.batchName}</div>
            <div className="batch-info-list">
              <div className="batch-info-item">
                <SolutionOutlined />
                <span className="batch-info-text">{instructorName}</span>
              </div>
              <div className="batch-info-item">
                <CalendarOutlined />
                <span className="batch-info-text">{batch.startTime || '--'} – {batch.endTime || '--'}</span>
              </div>
              <div className="batch-info-item">
                <TeamOutlined />
                <span className="batch-info-text">{studentsCount} student{studentsCount !== 1 ? 's' : ''} enrolled</span>
              </div>
            </div>

            {/* CTA Footer */}
            {pageAccess.isView && (
              <div className="batch-card-cta">
                <Button type="primary" className="batch-btn-view-details" block onClick={() => { handleView(batch) }}>
                  View Details
                </Button>
              </div>
            )}
          </div>
        </Card>
      </Col>
    );
  };

  const filterFields = useMemo(() => {
    const instructorList = instructors?.users || [];
    return [
      {
        name: 'instructorId', label: 'Instructor', type: 'select', placeholder: 'Select Instructor',
        options: instructorList.map((instructor: any) => ({ label: `${instructor.firstName} ${instructor.lastName}`, value: instructor._id, })),
      },
    ];
  }, [instructors]);

  useEffect(() => {
    const el = document.getElementById('batches-row-element');
    if (!el) return;
    const updateWidth = () => setBatchesRowWidth(el.clientWidth);
    updateWidth();
    const observer = new ResizeObserver(updateWidth);
    observer.observe(el);
    return () => observer.disconnect();
  }, [viewMode, filtered.length, batchesRowWidth]);

  useEffect(() => {
    const onResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const baseWidth = viewportWidth <= 1440 ? 915 : 1525;
  const sidePadding = Math.max(0, (batchesRowWidth - baseWidth) / 2);

  const gridHeaderStyle = {
    width: batchesRowWidth ? `${batchesRowWidth}px` : '100%',
    'padding': `8px 8px 8px 8px`,
  } as React.CSSProperties;

  if (getBatch.isLoading && !isTabSwitching) {
    return <SpinnerLoader />;
  }

  return (
    <div style={{ padding: "6px 9px" }}>
      <div>
        <Row justify="space-between" align="middle"
          className={`mb-4 row-batch-header ${viewMode !== 'calendar' ? 'row-batch-header--grid' : ''}`}
          style={viewMode === 'calendar' ? { margin: 0, padding: '5px 0' } : gridHeaderStyle}>
          <div className="category-toolbar" style={viewMode === 'calendar' ? {} : {}}>
            {viewMode === 'grid' && (
              <Input placeholder="Search by batch name or instructor" prefix={<SearchOutlined />}
                allowClear className="search-input batch-input" value={searchText} onChange={(e) => setSearchText(e.target.value)} />
            )}
            {viewMode === 'grid' && (
              <FilterPopover
                appliedCount={Object.keys(appliedFilters).length}
                fields={filterFields}
                onApply={handleApplyFilter}
                onClear={handleClearFilter}
                initialValues={appliedFilters}
              />
            )}
            {viewMode === 'grid' && (
              <div className="batch-status-tabs">
                <Segmented
                  value={statusFilter}
                  onChange={(val) => handleStatusChange(val)}
                  options={[
                    {
                      label: (
                        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '0 4px' }}>
                          <span>All</span>
                          <Badge
                            count={statusCounts.ALL}
                            overflowCount={999}
                            showZero
                            style={{
                              backgroundColor: statusFilter === 'ALL' ? '#1677ff' : '#c7c2c2',
                              color: statusFilter === 'ALL' ? '#fff' : '#666',
                              fontSize: 11,
                              height: 18,
                              lineHeight: '18px',
                              minWidth: 18,
                              padding: '0 5px',
                              boxShadow: 'none',
                            }}
                          />
                        </div>
                      ),
                      value: 'ALL',
                    },
                    {
                      label: (
                        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '0 4px' }}>
                          <span>Active</span>
                          <Badge
                            count={statusCounts.ACTIVE}
                            overflowCount={999}
                            showZero
                            style={{
                              backgroundColor: statusFilter === 'ACTIVE' ? '#1677ff' : '#c7c2c2',
                              color: statusFilter === 'ACTIVE' ? '#fff' : '#666',
                              fontSize: 11,
                              height: 18,
                              lineHeight: '18px',
                              minWidth: 18,
                              padding: '0 5px',
                              boxShadow: 'none',
                            }}
                          />
                        </div>
                      ),
                      value: 'ACTIVE',
                    },
                    {
                      label: (
                        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '0 4px' }}>
                          <span>Inactive</span>
                          <Badge
                            count={statusCounts.INACTIVE}
                            overflowCount={999}
                            showZero
                            style={{
                              backgroundColor: statusFilter === 'INACTIVE' ? '#1677ff' : '#c7c2c2',
                              color: statusFilter === 'INACTIVE' ? '#fff' : '#666',
                              fontSize: 11,
                              height: 18,
                              lineHeight: '18px',
                              minWidth: 18,
                              padding: '0 5px',
                              boxShadow: 'none',
                            }}
                          />
                        </div>
                      ),
                      value: 'INACTIVE',
                    },
                    {
                      label: (
                        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '0 4px' }}>
                          <span>Completed</span>
                          <Badge
                            count={statusCounts.COMPLETED}
                            overflowCount={999}
                            showZero
                            style={{
                              backgroundColor: statusFilter === 'COMPLETED' ? '#1677ff' : '#c7c2c2',
                              color: statusFilter === 'COMPLETED' ? '#fff' : '#666',
                              fontSize: 11,
                              height: 18,
                              lineHeight: '18px',
                              minWidth: 18,
                              padding: '0 5px',
                              boxShadow: 'none',
                            }}
                          />
                        </div>
                      ),
                      value: 'COMPLETED',
                    },
                  ]}
                />
              </div>
            )}
          </div>

          <Col style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {/* View mode toggle */}
            {pageAccess.isCalendarView &&
              <div className="batch-view-toggle" style={{ marginRight: '20px' }}>
                <Tooltip title="Grid View">
                  <button
                    className={`batch-view-toggle-btn${viewMode === 'grid' ? ' active' : ''}`}
                    onClick={() => handleSetViewMode('grid')}
                  >
                    <AppstoreOutlined />
                  </button>
                </Tooltip>
                <Tooltip title="Calendar View">
                  <button
                    className={`batch-view-toggle-btn${viewMode === 'calendar' ? ' active' : ''}`}
                    onClick={() => handleSetViewMode('calendar')}
                  >
                    <CalendarOutlined />
                  </button>
                </Tooltip>
              </div>}
              {viewMode === 'grid' && pageAccess.isAdd && (
                <GuardedButton type="primary" icon={<PlusOutlined />} onClick={() => showModal()}> Add Batch</GuardedButton>
              )}
            </Col>

          </Row>

          {isTabSwitching ? (
            <SpinnerLoader />
          ) : viewMode === 'calendar' ? (
            <div style={{ padding: '0 16px 16px' }}>
              <CalendarBatchView
                batches={filtered}
                instructors={availableInstructors || []}
                onView={handleView}
                onDelete={(id) => { setRecordToDelete(id); setVisible(true); }}
                onAddBatch={() => showModal()}
              />
            </div>
          ) : filtered.length === 0 && !getBatch.isLoading ? (
            <Card className='empty-state-card' bordered={false}>
              <Empty description="No batches found" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            </Card>
          ) : (
            <div style={{ height: '85vh', width: '100%', display: 'flex', flexDirection: 'column', paddingLeft: 'var(--side-padding)', paddingRight: 'var(--side-padding)' }}>
              <Row gutter={[16, 16]} className="batches-row" id="batches-row-element" style={{ columnGap: "5px", margin: "0", paddingLeft: `${sidePadding}px`, paddingRight: `${sidePadding}px` }}>
                {filtered.map((batch: any) => renderBatchCard(batch))}
              </Row>
              <div className="batch-grid-pagination" style={{ position: window.innerWidth > 1300 ? 'absolute' : "static", bottom: 0, right: '30px', marginRight: pageSize >= 10 ? "20px" : "0px" }}>
                <Paginations total={getBatch?.batchdata?.pagination?.totalRecords} defaultCurrent={currentPage} defaultPageSize={pageSize} callback={handleOnPageChange} />
              </div>
            </div>
          )}
        </div>
      <BatchFormModal isLoading={batchData?.isLoading} open={isModalOpen} title={editing ? 'Edit Batch' : 'Add New Batch'} isEdit={!!editing} onCancel={() => { setIsModalOpen(false); setEditing(null); form.resetFields(); }} onFinish={onFinish} form={form} />
      <ConfirmModal centered content="Are you sure you want to delete this batch?" visible={visible}
        cancelText='Cancel' okText='Delete' title='Confirm Delete' onOk={handleDelete} onCancel={() => setVisible(false)} />
    </div>
  );
};

export default Batches;
